import { ComponentObservable, componentObserver } from '../../component-observer';
import { createHtmlElementFromString, getFallbackIconForCategory, formatText } from '../../utils/html.utils';
import { Icon } from '../../general/icons/Icon';
import {
  deleteItemFromShoppingCart,
  getCurrency,
  getPrice,
  getPriceWithCurrencyForOnePiece,
  getShoppingCart
} from '../../api/shopping-cart-api';
import { getUrlForImage } from '../../utils/image.utils';
import { HamiltonProductPrice } from '../../api/products-api';
import { Param, writeToUrl } from '../../utils/url.utils';
import { breakpointListener } from '../../utils/breakpoint-listener.module';
import { allowScrollingOnBody, preventScrollingOnBody } from '../../utils/body-scroll.utils';
import { currentBreakpoint } from '../../utils/resize.utils';
import { toastNotification } from '../toast-notification/toast-notification.module';

/**
 * Object that holds the data for a shopping cart item.
 */
export interface HamiltonShoppingCartItem {
  /**
   * ID that we give a product in a shopping cart. Use this property to delete an
   * item from the shopping cart.
   */
  '@name': string;

  /**
   * How often this product was added to the shopping cart.
   */
  quantity: string;

  /**
   * The product info for this cart item.
   */
  article: HamiltonShoppingCartProduct;
}

export interface HamiltonShoppingCartProduct {
  /**
   * ID of the product, which is used to add a product to the shopping cart.
   */
  '@id': string;
  /**
   * Name of the product, that is displayed to the user.
   */
  name: string;
  partNumber: string;
  image: {
    renditions: {
      small: {
        link: string;
      };
      large: {
        link: string;
      };
    };
  };
  price: HamiltonProductPrice[];
  icons: string[];
}

/**
 * Initializes the shopping cart elements.
 */
class ShoppingCartModule implements ComponentObservable {
  /**
   * Selector of all elements that must be initialized in javascript.
   */
  componentSelector = '.shopping-cart';

  /**
   * Class that hides/displays the shopping cart
   */
  visibleClass = 'shopping-cart--visible';

  /**
   * Count of the skeleton elements to render
   */
  skeletonRows = 2;

  /**
   * Markup for the shopping cart item.
   */
  shoppingCartItemMarkup = `
  <div class="shopping-cart-item">
      <img src="" alt="Image of shopping cart item"/>
      <div class="shopping-cart-item__content">
        <span class="shopping-cart-item__product-id"></span>
        <a class="shopping-cart-item__product-label" role="button"></a>
        <div class="shopping-cart-item__price-and-quantity">
          <span class="shopping-cart-item__product-price"></span>
        </div>
        <span class="shopping-cart-item__remove-item-button">${Icon.delete}</span>
      </div>
  </div>
  `;

  /**
   * Create the shopping cart. Fetches the shopping cart and renders it.
   * @param shoppingCartElement: The root element of the shopping cart.
   */
  createShoppingCart(shoppingCartElement: HTMLElement): void {
    const shopingCartDataPromise = this.fetchShoppingCartData();
    if (shopingCartDataPromise) {
      shopingCartDataPromise.then((shoppingCartData) => {
        if (shoppingCartData !== null) {
          // Render table
          this.renderShoppingCartWithData(shoppingCartData, shoppingCartElement);

          formatText(shoppingCartElement);
        } else {
          // hide the shopping cart button
          this.hideTriggerButton();
        }
      });

      // Render loading state while waiting for data
      this.renderShoppingCartWithData(null, shoppingCartElement);
    }
  }

  /**
   * Retrieve the shopping cart data.
   */
  fetchShoppingCartData(): Promise<HamiltonShoppingCartItem[]> {
    return getShoppingCart();
  }

  /**
   * Render the shopping cart.
   * @param shoppingCartItems: List of cart items to render.
   * @param shoppingCartElement: The shopping cart root element.
   */
  renderShoppingCartWithData(shoppingCartItems: HamiltonShoppingCartItem[], shoppingCartElement: HTMLElement): void {
    const breakpoint = currentBreakpoint();
    if (breakpoint.isMobileAny) {
      preventScrollingOnBody();
    }

    // This is the container for all the shopping carts
    const shoppingCartItemsContainer = shoppingCartElement.querySelector(
      '.shopping-cart__items-wrapper'
    ) as HTMLElement;

    // clear the content before adding new elements
    shoppingCartItemsContainer.innerHTML = '';

    // Render shopping carts or empty state
    let totalPrice = 0;
    let totalPriceCurrency = '';
    if (shoppingCartItems) {
      for (const cartItem of shoppingCartItems) {
        const product = cartItem.article;

        const shoppingCartItemMarkup = createHtmlElementFromString(this.shoppingCartItemMarkup);
        const shoppingCartItemContent = shoppingCartItemMarkup.querySelector('.shopping-cart-item__content');
        const productName = shoppingCartItemMarkup.querySelector<HTMLElement>('.shopping-cart-item__product-label');
        productName.innerText = product?.name || `${product}`;
        productName.addEventListener('click', () => {
          writeToUrl({ param: Param.PRODUCT, value: product['@id'] });
          this.toggleShoppingCartVisibleState(shoppingCartElement);
        });
        shoppingCartItemMarkup.querySelector<HTMLElement>('.shopping-cart-item__product-id').innerText =
          product?.partNumber;

        const imageUrl = getUrlForImage(product.image?.renditions?.small?.link);
        const itemImage = shoppingCartItemMarkup.querySelector('img');
        if (imageUrl) {
          // if there is an image for the product, show it
          itemImage.src = imageUrl;
        } else {
          // otherwise show the large version of the category icon for this product
          shoppingCartItemMarkup.removeChild(itemImage);
          const categoryIconLarge = getFallbackIconForCategory(product.icons, true);
          shoppingCartItemMarkup.insertBefore(categoryIconLarge, shoppingCartItemContent);
        }

        if (product.price != undefined && product.price.length > 0) {
          shoppingCartItemMarkup.querySelector<HTMLElement>('.shopping-cart-item__product-price').innerText =
            getPriceWithCurrencyForOnePiece(product.price);

          // sum the prices for each item
          totalPrice += getPrice(product.price);
          totalPriceCurrency = getCurrency(product.price);
        }

        this.addListenerForDeleteFromCartButton(shoppingCartElement, shoppingCartItemMarkup, cartItem['@name']);

        shoppingCartItemsContainer.appendChild(shoppingCartItemMarkup);
      }

      shoppingCartElement.classList.toggle('shopping-cart--empty', shoppingCartItems?.length === 0);
    } else {
      this.renderLoadingState(shoppingCartItemsContainer);
    }

    // render the total amount
    const totalAmountValue = shoppingCartElement.querySelector<HTMLElement>('.shopping-cart__total');

    if (!shoppingCartItems) {
      // skeleton for the total amount
      totalAmountValue.innerHTML = '<span class="shopping-cart__skeleton"></span>';
    } else {
      totalAmountValue.innerText = `${totalPrice.toFixed(2)} ${totalPriceCurrency}`;
    }
  }

  /**
   * Display skeletons until the actual content is loaded.
   */
  renderLoadingState(shoppingCartItemsContainer: HTMLElement): void {
    for (let i = 0; i < this.skeletonRows; i++) {
      const shoppingCartItemMarkup = createHtmlElementFromString(this.shoppingCartItemMarkup);
      const skeleton = `<span class="shopping-cart__skeleton${i !== 0 ? '-delay' : ''}"></span>`;
      shoppingCartItemMarkup.querySelector<HTMLElement>('.shopping-cart-item__product-label').innerHTML = skeleton;
      shoppingCartItemMarkup.querySelector<HTMLElement>('.shopping-cart-item__product-price').innerHTML = skeleton;
      shoppingCartItemMarkup.querySelector<HTMLElement>('.shopping-cart-item__product-id').innerHTML = skeleton;

      // skeleton image
      const shoppingCartItemImg = shoppingCartItemMarkup.querySelector('img');
      const skeletonImage = document.createElement('div');
      skeletonImage.classList.add(`shopping-cart__skeleton-image${i !== 0 ? '-delay' : ''}`);
      shoppingCartItemMarkup.insertBefore(skeletonImage, shoppingCartItemImg);
      shoppingCartItemImg.remove();

      // hide the delete button
      shoppingCartItemMarkup.querySelector<HTMLElement>('.shopping-cart-item__remove-item-button').remove();

      shoppingCartItemsContainer.appendChild(shoppingCartItemMarkup);
    }
  }

  /**
   * Listen for clicks on the 'delete' button, then removes the item from the cart.
   */
  addListenerForDeleteFromCartButton(root: HTMLElement, shoppingCartItem: HTMLElement, productId: string): void {
    const deleteFromCartButton = shoppingCartItem.querySelector('.shopping-cart-item__remove-item-button');

    if (deleteFromCartButton) {
      deleteFromCartButton.addEventListener('click', () => {
        deleteItemFromShoppingCart(productId).then((success) => {
          if (success) {
            this.createShoppingCart(root);
          } else {
            const errorMessage = root.dataset.errorMessage;
            toastNotification.createErrorToastMessage(errorMessage);
          }
        });
      });
    }
  }

  /**
   * Initialize all shopping carts on the current page.
   * @param observe Boolean if the ShoppingCartModule should listen for changes in the DOM and initialize dynamically
   *   added elements
   */
  initializeAll(observe: boolean): void {
    const allComponents = document.querySelectorAll(this.componentSelector);
    for (let i = 0; i < allComponents.length; i++) {
      this.initialize(allComponents[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize elements when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Show/hide the shopping cart when the trigger button was clicked.
   */
  toggleShoppingCartVisibleState(root: HTMLElement): void {
    root.classList.toggle(this.visibleClass);
    // set aria attribute
    const isHidden = !root.classList.contains(this.visibleClass);
    root.setAttribute('aria-hidden', isHidden.toString());

    if (!isHidden) {
      // reload the shopping cart before showing it
      this.createShoppingCart(root);
    } else {
      // deselect the shopping cart button
      this.deselectTriggerButton();
      // allow scrolling on body again
      allowScrollingOnBody();
    }
  }

  /**
   * Set the selected state of the trigger button. Adds the primary state (blue) of the button
   * that shows that the shopping cart is currently open.
   * @param triggerButton: The trigger button HTML-Element
   * @param selected: If the button should be selected.
   */
  setTriggerButtonSelectedState(triggerButton: HTMLElement, selected: boolean): void {
    triggerButton.classList.toggle('button--primary', selected);
  }

  /**
   * Hides button that open the shopping cart.
   */
  hideTriggerButton(): void {
    document.documentElement.classList.add('user-logged-out');
  }

  /**
   * Deselect the trigger button. Removes the primary state (blue) after the shopping cart
   * was closed.
   */
  deselectTriggerButton(): void {
    const triggerButton = document.querySelector('#header-button-cart');
    triggerButton.classList.remove('button--primary');
  }

  /**
   * Open the shopping cart if it is not visible yet.
   */
  openShoppingCart(): void {
    const shoppingCartElement = document.querySelector<HTMLElement>('.shopping-cart');
    const isHidden = !shoppingCartElement.classList.contains(this.visibleClass);

    if (isHidden) {
      this.toggleShoppingCartVisibleState(shoppingCartElement);
    }
  }

  /**
   * Reload the shopping cart if it is open.
   */
  reloadShoppingCart(): void {
    const shoppingCartElement = document.querySelector<HTMLElement>('.shopping-cart');
    const isHidden = !shoppingCartElement.classList.contains(this.visibleClass);

    if (!isHidden) {
      // reload the shopping cart before showing it
      this.createShoppingCart(shoppingCartElement);
    }
  }

  /**
   * Close an open shopping cart.
   */
  closeShoppingCart(): void {
    const shoppingCartElement = document.querySelector<HTMLElement>('.shopping-cart');
    shoppingCartElement.classList.remove(this.visibleClass);
    allowScrollingOnBody();
  }

  /**
   * Initialize open shoppingCart trigger (button). Opens the sidebar on click only on mobile.
   * @param root The shopping cart root element
   */
  initializeOpenShoppingCartTrigger(root: HTMLElement): void {
    const shoppingCartId = root.getAttribute('id');
    const triggerButton = document.querySelector<HTMLElement>(`[data-shopping-cart-id='${shoppingCartId}']`);
    if (triggerButton) {
      triggerButton.addEventListener('click', () => {
        this.toggleShoppingCartVisibleState(root);

        const isVisible = root.classList.contains(this.visibleClass);
        this.setTriggerButtonSelectedState(triggerButton, isVisible);
      });
    }
  }

  /**
   * Initialize the given shopping cart element.
   * @param root The shopping cart root element that should be initialized
   */
  initialize(root: HTMLElement): void {
    componentObserver.markElementAsInitialized(this.componentSelector, root);

    // listen for clicks on the close button
    root
      .querySelector('.shopping-cart__close-button')
      .addEventListener('click', this.toggleShoppingCartVisibleState.bind(this, root), false);

    // Add ESC key listener to close shopping cart
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && root.classList.contains(this.visibleClass)) {
        this.closeShoppingCart();
      }
    };

    // Add the event listener to document when shopping cart becomes visible
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains(this.visibleClass)) {
            document.addEventListener('keydown', handleEscKey);
          } else {
            document.removeEventListener('keydown', handleEscKey);
          }
        }
      });
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class']
    });

    // close the shopping cart when breakpoint changes from mobile -> desktop and the other way round
    breakpointListener.listenForBreakpointChangeBetweenMobileAndDesktop(() => {
      this.closeShoppingCart();
    });

    this.initializeOpenShoppingCartTrigger(root);
    this.createShoppingCart(root);
  }
}

const shoppingCartModule = new ShoppingCartModule();
export { shoppingCartModule as shoppingCart };
